import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import { 
  CheckCircle, 
  Clock, 
  XCircle, 
  AlertCircle,
  Pause,
  Play,
  LucideIcon
} from "lucide-react"

type StatusType = 
  | "success" | "pending" | "failed" | "warning" 
  | "active" | "inactive" | "approved" | "rejected"
  | "sent" | "delivered" | "processing"

interface StatusConfig {
  label: string
  variant: "default" | "secondary" | "destructive" | "success" | "warning" | "info"
  icon: LucideIcon
  className?: string
}

const statusConfigs: Record<StatusType, StatusConfig> = {
  success: {
    label: "成功",
    variant: "success",
    icon: CheckCircle,
    className: "text-green-700 bg-green-100"
  },
  pending: {
    label: "待处理",
    variant: "warning", 
    icon: Clock,
    className: "text-yellow-700 bg-yellow-100"
  },
  failed: {
    label: "失败",
    variant: "destructive",
    icon: XCircle,
    className: "text-red-700 bg-red-100"
  },
  warning: {
    label: "警告",
    variant: "warning",
    icon: AlertCircle,
    className: "text-orange-700 bg-orange-100"
  },
  active: {
    label: "活跃",
    variant: "success",
    icon: Play,
    className: "text-green-700 bg-green-100"
  },
  inactive: {
    label: "非活跃",
    variant: "secondary",
    icon: Pause,
    className: "text-gray-700 bg-gray-100"
  },
  approved: {
    label: "已通过",
    variant: "success", 
    icon: CheckCircle,
    className: "text-green-700 bg-green-100"
  },
  rejected: {
    label: "已拒绝",
    variant: "destructive",
    icon: XCircle,
    className: "text-red-700 bg-red-100"
  },
  sent: {
    label: "已发送",
    variant: "info",
    icon: CheckCircle,
    className: "text-blue-700 bg-blue-100"
  },
  delivered: {
    label: "已送达",
    variant: "success",
    icon: CheckCircle,
    className: "text-green-700 bg-green-100"
  },
  processing: {
    label: "处理中",
    variant: "warning",
    icon: Clock,
    className: "text-yellow-700 bg-yellow-100"
  }
}

interface StatusBadgeProps {
  status: StatusType
  showIcon?: boolean
  className?: string
  size?: "sm" | "md" | "lg"
}

export function StatusBadge({ 
  status, 
  showIcon = true, 
  className,
  size = "md" 
}: StatusBadgeProps) {
  const config = statusConfigs[status]
  const Icon = config.icon

  const sizeClasses = {
    sm: "text-xs px-2 py-0.5",
    md: "text-xs px-2.5 py-0.5", 
    lg: "text-sm px-3 py-1"
  }

  const iconSizes = {
    sm: "h-3 w-3",
    md: "h-3 w-3",
    lg: "h-4 w-4"
  }

  return (
    <Badge 
      variant={config.variant}
      className={cn(
        "inline-flex items-center gap-1 font-medium",
        config.className,
        sizeClasses[size],
        className
      )}
    >
      {showIcon && <Icon className={iconSizes[size]} />}
      {config.label}
    </Badge>
  )
}

// 预定义的状态映射函数
export function getTemplateStatusBadge(status: string) {
  const statusMap: Record<string, StatusType> = {
    'PENDING': 'pending',
    'APPROVED': 'approved', 
    'REJECTED': 'rejected'
  }
  return statusMap[status] || 'pending'
}

export function getSmsStatusBadge(status: string) {
  const statusMap: Record<string, StatusType> = {
    'PENDING': 'pending',
    'SENT': 'sent',
    'DELIVERED': 'delivered',
    'FAILED': 'failed'
  }
  return statusMap[status] || 'pending'
}

export function getAccountStatusBadge(status: string) {
  const statusMap: Record<string, StatusType> = {
    'ACTIVE': 'active',
    'INACTIVE': 'inactive',
    'SUSPENDED': 'warning'
  }
  return statusMap[status] || 'inactive'
}